import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import HLogoutModal from "./HLogoutModal";
import { X } from "lucide-react";
import * as localService from "../services/localService";
import { Constants, RoleConstants } from "../utils/constants";
import { getNavigationItems } from "../utils/navigationUtils";
import toastUtils from "../utils/toast";
import { FeatureAccess } from "../utils/featureAccessControl";
import logger from "../utils/logger";


export default function HSidebarMobile({ isOpen, onClose }) {
  const location = useLocation();
  const history = useHistory();

  // Read user role from localStorage (same as HSidebarPro)
  const userRoleCode = useMemo(() => {
    const userData = localService.getUser();
    return userData?.organizationRole?.code;
  }, []);

  const [userInitials, setUserInitials] = useState("");
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userImageUrl, setUserImageUrl] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isAdminRole = useCallback(() => {
    return RoleConstants.ROLE_GROUPS.UPGRADE_ALLOWED_ROLES.includes(userRoleCode);
  }, [userRoleCode]);

  const isIndividualCustomer = userRoleCode === "ROLE_HOST_CUSTOMER_INDIVIDUAL";

  const handleSubscriptionClick = useCallback(() => {
    if (!isAdminRole()) {
      toastUtils.warning("Contact Admin", {
        description: "Contact your admin to upgrade your account to access more features.",
      });
      return;
    }
    history.push("/host/pricing");
    onClose();
  }, [isAdminRole, history, onClose]);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const userData = localService.getUser();
        if (userData) {
          const { firstName, lastName, role, organizationRole, imageUrl } = userData;
          const initials = getInitials(firstName, lastName);
          setUserInitials(initials);
          setUserName(`${firstName || ""} ${lastName || ""}`.trim());
          setUserImageUrl(imageUrl || "");
          const userRole = organizationRole?.name || role || "User";
          setUserRole(userRole || "Free Account");
        }
      } catch (e) {
        logger.error("Error loading user data in mobile sidebar:", e);
      }
    };

    loadUserData();

    const handleStorageChange = e => {
      if (e.key === "currentUser" || e.key === "user") {
        loadUserData();
      }
    };

    const handleProfileUpdate = () => {
      loadUserData();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("profileUpdated", handleProfileUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("profileUpdated", handleProfileUpdate);
    };
  }, []);

  const { mainNavItems, toolsItems } = useMemo(() => getNavigationItems(), []);

  const isActive = path => location.pathname === path;

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      localService.clearAllUserData();
      setShowLogoutModal(false);
      history.push("/signin");
    } catch (error) {
      logger.error("Error during logout:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleLinkClick = () => {
    onClose(); // Close sidebar when navigating
  };

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {isOpen && <div className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden" onClick={onClose} />}

      {/* Mobile Sidebar (overlay sheet) */}
      <div
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <button
            onClick={() => {
              history.push("/host/dashboard");
              onClose();
            }}
            className="text-2xl font-extrabold tracking-[0.25em] text-slate-900"
          >
            SPACIA
          </button>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3">
          {/* Main Navigation */}
          <div className="px-3 space-y-0.5">
            {mainNavItems.map(item => (
              <MobileNavItem
                key={item.path}
                icon={item.icon}
                label={item.name}
                path={item.path}
                isActive={isActive(item.path)}
                onClick={handleLinkClick}
              />
            ))}
          </div>

          {/* Tools Section */}
          <div className="mt-4 px-3">
            <h3 className="text-xs font-medium tracking-wider text-gray-500 uppercase mb-2">TOOLS</h3>
            <div className="space-y-0.5">
              {toolsItems.map(item => (
                <MobileNavItem
                  key={item.path}
                  icon={item.icon}
                  label={item.name}
                  path={item.path}
                  isActive={isActive(item.path)}
                  onClick={handleLinkClick}
                />
              ))}
            </div>
          </div>
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-gray-200 p-3 space-y-3">
          {/* Upgrade Plan Section */}
          {FeatureAccess.shouldShowSubscriptionAndPurchasesButton(localService.getUser()) && (
            <div className="p-2 text-center bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="flex justify-center mb-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={Constants.HOST_EXPERIENCE_BASE_COLOR}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                </svg>
              </div>
              <button
                onClick={handleSubscriptionClick}
                className="w-full px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
              >
                Subscriptions & Purchases
              </button>
            </div>
          )}

          {/* User Profile Section */}
          <div
            className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => {
              history.push("/host/settings");
              onClose();
            }}
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden bg-gray-100">
              <img
                src={
                  userImageUrl ||
                  "https://ui-avatars.com/api/?name=" + encodeURIComponent(userName || "User") + "&background=random"
                }
                alt={userName || "User"}
                className="object-cover w-full h-full"
                onError={e => {
                  e.target.onerror = null;
                  if (userInitials) {
                    const parent = e.target.parentNode;
                    e.target.style.display = "none";
                    const initialsSpan = document.createElement("span");
                    initialsSpan.className =
                      "text-sm font-medium text-gray-700 flex items-center justify-center w-full h-full";
                    initialsSpan.textContent = userInitials;
                    parent.appendChild(initialsSpan);
                  } else {
                    e.target.src =
                      "https://ui-avatars.com/api/?name=" +
                      encodeURIComponent(userName || "User") +
                      "&background=random";
                  }
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 truncate">{userName || "User"}</p>
              <p className="text-xs text-gray-500 truncate">{userRole}</p>
            </div>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <img src="/img/svgs/logout.svg" alt="Logout" className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <HLogoutModal
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        isLoading={isLoggingOut}
      />
    </>
  );
}

function getInitials(firstName, lastName) {
  let initials = "";
  if (firstName) initials += firstName.charAt(0);
  if (lastName) initials += lastName.charAt(0);
  return initials.toUpperCase();
}

function MobileNavItem({ icon: Icon, label, path, isActive, onClick }) {
  return (
    <Link
      to={path}
      onClick={onClick}
      className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs font-medium transition-colors hover:bg-gray-100 hover:text-gray-900"
      style={{
        backgroundColor: isActive ? Constants.HOST_EXPERIENCE_BASE_COLOR : "transparent",
        color: isActive ? "#ffffff" : "#000000",
      }}
    >
      <Icon className="w-4 h-4" color={isActive ? "#ffffff" : "#000000"} />
      <span>{label}</span>
    </Link>
  );
}
